<template>
  <PageWrapper :isLoading="isLoading" hasFilters @submitFilters="applyFilters">
    <template #title> لیست تمام نظرات </template>

    <template #titleBtn>
      <BButton variant="success" @click="openCreateModal">
        ایجاد نظر
      </BButton>
    </template>

    <template #filters>
      <div class="filters-width comment-filter">
        <BFormGroup label="وضعیت نمایش" label-class="">
          <BFormSelect v-model="ui.confirmed" :options="confirmedOptions" />
        </BFormGroup>
      </div>

      <div class="filters-width comment-filter">
        <BFormGroup label="نوع مدیا" label-class="">
          <BFormSelect v-model="ui.mediaType" :options="mediaTypeOptions" />
        </BFormGroup>
      </div>

      <div class="filters-width comment-filter">
        <BFormGroup label="نوع نظر" label-class="">
          <BFormSelect v-model="ui.type" :options="typeOptions" />
        </BFormGroup>
      </div>

      <!-- ریزتر: کدام استاد / درس / پکیج (هر انتخابگر عنوان خودش را دارد) -->
      <div v-if="ui.type" :key="`object-picker-${ui.type}-${pickerResetKey}`" class="filters-width comment-filter comment-filter--picker">
        <MasterPicker v-if="Number(ui.type) === 5" @getMaster="setSelectedMaster" />
        <LessonPicker v-else-if="Number(ui.type) === 3" @getLesson="setSelectedLesson" />
        <LessonCategoryPicker v-else-if="Number(ui.type) === 6" closeOnSelect @getLessonCategory="setSelectedPackageCategory" />
      </div>

      <div class="filters-width comment-filter">
        <BFormGroup label="جستجو در متن نظر" label-class="mb-25">
          <BFormInput
              v-model="ui.searchComFermand"
              placeholder="بخشی از متن نظر..."
              @keyup.enter="applyFilters"
          />
        </BFormGroup>
      </div>
    </template>

    <BCard>
      <CustomTable
          class="comments-table"
          :pageNumber="list.pageNumber"
          :count="list.count"
          :totalCount="totalCount"
          :tableColumns="myTableColumns"
          :tableItems="tableItems"
          @changePage="changePage"
      >
        <template #select="data">
          <BFormCheckbox
              :checked="selectedIds.includes(data.item.commentId)"
              @change="toggleSelect(data.item.commentId, $event)"
          />
        </template>

        <template #commentId="data">
          {{ data.item.commentId }}
        </template>

        <template #userFullName="data">
          <span class="text-nowrap">{{ getUserFullName(data.item) }}</span>
          <BBadge v-if="data.item.isVip || data.item.IsVip" variant="warning" class="ml-25">VIP</BBadge>
        </template>

        <!-- نوع نظر (استاد/درس/پکیج) بالای اسم همان مورد -->
        <template #product="data">
          <small class="d-block text-muted">{{ getTypeLabelUI(data.item.type) }}</small>
          <span class="comment-product">{{ getProductDisplay(data.item) }}</span>
        </template>

        <template #content="data">
          <div class="comment-cell">
            <!-- با کلیک، مودال ویرایش باز و به بخش «شکل نهایی» اسکرول می‌شود تا مدیا دیده یا پخش شود -->
            <BBadge
                v-if="hasMedia(data.item)"
                tag="button"
                type="button"
                variant="light-primary"
                class="mb-25 comment-media-badge"
                :title="`نمایش ${getMediaLabel(data.item.mediaType)}`"
                @click="openEditModal(data.item, true)"
            >
              {{ getMediaLabel(data.item.mediaType) }}
            </BBadge>

            <!-- متن بلند دو خطی با سه‌نقطه؛ متن کامل در مودال -->
            <template v-if="String(data.item.message || '').trim()">
              <div
                v-if="isLongMessage(data.item.message)"
                class="comment-text comment-text--clamp"
                role="button"
                tabindex="0"
                @click="openMessage(data.item)"
                @keydown.enter.prevent="openMessage(data.item)"
              >{{ String(data.item.message).trim() }}</div>
              <div v-else class="comment-text">{{ String(data.item.message).trim() }}</div>
            </template>

            <div v-else-if="!hasMedia(data.item)" class="text-muted" style="font-size: 12px">
              بدون متن
            </div>

            <!-- تاریخ و «بیشتر» در یک خط تا ردیف کوتاه‌تر شود -->
            <div class="comment-meta">
              <small class="comment-date">{{ formatDateTimeNoSeconds(data.item.createDate) }}</small>
              <button
                v-if="isLongMessage(data.item.message)"
                type="button"
                class="comment-more"
                @click="openMessage(data.item)"
              >
                بیشتر
              </button>
            </div>
          </div>
        </template>

        <template #priority="data">
          {{ Number((data.item.priority !== undefined && data.item.priority !== null) ? data.item.priority : ((data.item.Priority !== undefined && data.item.Priority !== null) ? data.item.Priority : 0)) }}
        </template>

        <template #isConfirmed="data">
          <BFormCheckbox
              switch
              :checked="!!data.item.isConfirmed"
              @change="toggleConfirm(data.item, $event)"
          />
        </template>

        <template #actions="data">
          <div class="d-flex align-items-center justify-content-center" style="gap: 6px">
            <BButton
                variant="transparent"
                size="sm"
                class="icon-btn text-primary"
                @click="openEditModal(data.item)"
                title="ویرایش"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </BButton>

            <BButton
                variant="transparent"
                size="sm"
                class="icon-btn text-danger"
                @click="deleteComment(data.item)"
                title="حذف"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </BButton>
          </div>
        </template>
      </CustomTable>
    </BCard>

    <BModal
        v-model="showMessageModal"
        centered
        hide-footer
        size="md"
        :title="messageItem ? getUserFullName(messageItem) : ''"
        @hidden="messageItem = null"
    >
      <template v-if="messageItem">
        <small class="d-block text-muted mb-1">
          {{ getTypeLabelUI(messageItem.type) }}: {{ getProductDisplay(messageItem) }}
          · {{ formatDateTimeNoSeconds(messageItem.createDate) }}
        </small>
        <p class="comment-full-text">{{ String(messageItem.message || "").trim() }}</p>
      </template>
    </BModal>

    <CommentCreateEditModal
        :value="showCreateModal"
        @input="showCreateModal = $event"
        :editing-item="editingItem"
        :scroll-to-preview="editScrollToPreview"
        :comment-type-options="commentTypeOptions"
        :media-type-options-u-i="mediaTypeOptionsUI"
        @submit="submitComment"
        @hidden="onCreateModalHidden"
        @refresh-list="refreshList"
    />
  </PageWrapper>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import { GetAll, CreateComment, UpdateComment, DeleteBy } from "@/libs/Api/Comments"
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormCheckbox,
  BFormSelect,
  BBadge,
  BFormInput,
  BFormGroup,
  BModal,
} from "bootstrap-vue"

import PageWrapper from "@/views/components/Utils/PageWrapper.vue"
import CustomTable from "@/views/components/Utils/CustomTable.vue"

import CommentCreateEditModal from "@/@core/components/comments/CreateEditModal.vue"

import MasterPicker from "@/views/components/MasterActiveCourse/MasterPicker.vue"
import LessonPicker from "@/views/components/Lessons/LessonPicker.vue"
import LessonCategoryPicker from "@/views/components/LessonCategories/LessonCategoryPicker.vue"

import {
  formatDateTimeNoSeconds,
  getTypeLabel,
  getMediaLabel,
  getUserFullName,
  getProductDisplay,
  hasMedia,
} from "@/@core/utils/commentUtils"

export default {
  name: "CommentsIndex",
  components: {
    PageWrapper,
    CustomTable,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormCheckbox,
    BFormSelect,
    BBadge,
    BFormInput,
    BFormGroup,
    BModal,
    CommentCreateEditModal,
    MasterPicker,
    LessonPicker,
    LessonCategoryPicker,
  },

  mounted() {
    this.applyFilters()
  },

  data() {
    return {
      isLoading: false,
      list: { pageNumber: 1, count: 10 },
      totalCount: 0,
      commentsRaw: [],
      selectedIds: [],
      showMessageModal: false,
      messageItem: null,
      pickerResetKey: 0,

      ui: {
        confirmed: null,
        mediaType: null,
        type: null,
        objectId: "",
        searchComFermand: "",
      },

      applied: {
        confirmed: null,
        mediaType: null,
        type: null,
        objectId: null,
        searchComFermand: "",
      },

      selectedTitles: { master: "", lesson: "", pkg: "" },
      appliedTitles: { master: "", lesson: "", pkg: "" },

      showCreateModal: false,
      editingItem: null,
      editScrollToPreview: false,

      confirmedOptions: [
        { text: "همه", value: null },
        { text: "تایید شده", value: true },
        { text: "تایید نشده", value: false },
      ],

      // CommentMediaTypes بکند؛ «همه» یعنی پارامتر فرستاده نمی‌شود
      mediaTypeOptions: [
        { text: "همه", value: null },
        { text: "متن", value: 1 },
        { text: "تصویر", value: 2 },
        { text: "صدا", value: 3 },
        { text: "ویدیو", value: 4 },
      ],

      typeOptions: [
        { text: "همه", value: null },
        { text: "استاد", value: 5 },
        { text: "درس", value: 3 },
        { text: "پکیج درسی", value: 6 },
      ],

      commentTypeOptions: [
        { name: "استاد", value: 5 },
        { name: "درس", value: 3 },
        { name: "پکیج درسی", value: 6 },
      ],

      mediaTypeOptionsUI: [
        { name: "تصویر", value: 2 },
        { name: "ویدیو", value: 4 },
        { name: "صوت", value: 3 },
      ],

      // جمع‌وجور: نوع نظر کنار محصول، VIP کنار نام و تاریخ زیر پیام
      myTableColumns: [
        { key: "commentId", label: "شناسه" },
        { key: "userFullName", label: "کاربر" },
        { key: "product", label: "مربوط به" },
        { key: "content", label: "پیام" },
        { key: "priority", label: "اولویت" },
        { key: "isConfirmed", label: "نمایش در سایت" },
        { key: "actions", label: "عملیات" },
      ],
    }
  },

  computed: {
    tableItems() {
      return Array.isArray(this.commentsRaw) ? this.commentsRaw : []
    },

    appliedObjectLabel() {
      const t = Number(this.applied.type)
      const id = Number(this.applied.objectId || 0)
      if (!t || !id) return ""
      if (t === 5) return this.appliedTitles.master || `#${id}`
      if (t === 3) return this.appliedTitles.lesson || `#${id}`
      if (t === 6) return this.appliedTitles.pkg || `#${id}`
      return `#${id}`
    },

    appliedBadgeText() {
      const parts = []
      const f = this.applied
      if (f.searchComFermand) parts.push(`جستجو: ${f.searchComFermand}`)
      if (f.confirmed !== null) parts.push(f.confirmed ? "تایید شده" : "تایید نشده")
      if (f.mediaType) {
        const media = this.mediaTypeOptions.find((x) => x.value === f.mediaType)
        parts.push(`مدیا: ${media ? media.text : f.mediaType}`)
      }
      if (Number(f.type)) parts.push(`نوع: ${this.getTypeLabelUI(f.type)}`)
      if (Number(f.objectId) && Number(f.type)) parts.push(`انتخاب: ${this.appliedObjectLabel}`)
      return parts.join(" | ")
    },
  },

  watch: {
    "ui.type"() {
      this.clearObjectSelection()
    },
  },

  methods: {
    formatDateTimeNoSeconds,
    isLongMessage(message) {
      return String(message || "").trim().length > 70
    },
    openMessage(item) {
      this.messageItem = item
      this.showMessageModal = true
    },
    getMediaLabel,
    getUserFullName,
    getProductDisplay(item) {
      const type = Number(item.type)
      const objectId = Number(item.objectId || 0)

      if (!type || !objectId) return "-"

      let title = String(item.objectTitle || "").trim()

      if (!title) {
        if (type === 5) {
          title = String(item.masterFullName || item.masterName || "").trim()
        } else if (type === 3) {
          title = String(item.lessonTitle || item.title || "").trim()
        } else if (type === 6) {
          title = String(item.categoryTitle || item.title || "").trim()
        }
      }

      return title || `#${objectId}`
    },
    hasMedia,

    getTypeLabelUI(type) {
      const t = Number(type)
      if (t === 5) return "استاد"
      if (t === 3) return "درس"
      if (t === 6) return "پکیج درسی"
      try {
        return getTypeLabel(type)
      } catch {
        return String(type ?? "")
      }
    },

    clearObjectSelection() {
      this.pickerResetKey += 1
      this.ui.objectId = ""
      this.selectedTitles.master = ""
      this.selectedTitles.lesson = ""
      this.selectedTitles.pkg = ""
    },

    clearAppliedTitles() {
      this.appliedTitles.master = ""
      this.appliedTitles.lesson = ""
      this.appliedTitles.pkg = ""
    },

    setSelectedMaster(master) {
      const id = Number(master?.userId || master?.id || 0)
      this.ui.objectId = id ? String(id) : ""
      const title = String(master?.fullName || master?.name || master?.title || "").trim()
      this.selectedTitles.master = title || (id ? `#${id}` : "")
    },

    setSelectedLesson(payload) {
      let id = 0
      let title = ""

      if (typeof payload === "object" && payload !== null) {
        id = Number(payload?.lessonId || payload?.id || payload?.value || payload?.courseLessonId || 0)
        title = String(payload?.title || payload?.lessonTitle || payload?.name || payload?.label || "").trim()

        if (!title && payload.data) {
          title = String(payload.data.title || payload.data.lessonTitle || payload.data.name || "").trim()
        }
      } else if (typeof payload === "number") {
        id = Number(payload)
      }

      this.ui.objectId = id ? String(id) : ""
      this.selectedTitles.lesson = title || (id ? `#${id}` : "")
    },

    setSelectedPackageCategory(payload) {
      let id = 0
      let title = ""

      if (typeof payload === "object" && payload !== null) {
        id = Number(payload?.id || payload?.value || payload?.lessonCategoryId || payload?.categoryId || 0)
        title = String(payload?.title || payload?.categoryTitle || payload?.name || payload?.label || "").trim()

        if (!title && payload.data) {
          title = String(payload.data.title || payload.data.categoryTitle || payload.data.name || "").trim()
        }
      } else if (typeof payload === "number") {
        id = Number(payload)
      }

      this.ui.objectId = id ? String(id) : ""
      this.selectedTitles.pkg = title || (id ? `#${id}` : "")
    },

    normalizeOk(res) {
      if (res?.isSuccess === true) return true
      if (res?.data?.isSuccess === true) return true
      if (res?.status === 200 || res?.status === 201) return true
      if (res?.statusCode === 200 || res?.statusCode === 201) return true
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) return true
      return false
    },

    unwrap(res) {
      if (!res) return res
      if (res?.data && (typeof res?.status === "number" || res?.headers)) return res.data
      return res
    },

    extractItems(res) {
      const root = this.unwrap(res)
      if (Array.isArray(root?.data?.data)) return root.data.data
      if (Array.isArray(root?.data)) return root.data
      if (Array.isArray(root)) return root
      return []
    },

    extractTotal(res) {
      const root = this.unwrap(res)
      const n = Number(root?.data?.total ?? root?.total ?? null)
      return Number.isFinite(n) && n >= 0 ? n : null
    },

    applyFilters() {
      const type = this.ui.type ? Number(this.ui.type) : null
      const objectId = Number(this.ui.objectId) ? Number(this.ui.objectId) : null

      this.applied = {
        confirmed: this.ui.confirmed,
        mediaType: this.ui.mediaType ? Number(this.ui.mediaType) : null,
        type,
        objectId: type && objectId ? objectId : null,
        searchComFermand: String(this.ui.searchComFermand || "").trim(),
      }

      this.clearAppliedTitles()
      if (this.applied.type === 5) this.appliedTitles.master = this.selectedTitles.master
      if (this.applied.type === 3) this.appliedTitles.lesson = this.selectedTitles.lesson
      if (this.applied.type === 6) this.appliedTitles.pkg = this.selectedTitles.pkg

      this.list.pageNumber = 1
      this.selectedIds = []
      this.getAllComments()
    },

    clearFilters() {
      this.ui = { confirmed: null, mediaType: null, type: null, objectId: "", searchComFermand: "" }
      this.clearObjectSelection()
      this.clearAppliedTitles()
      this.applyFilters()
    },

    buildQueryParams() {
      const params = {
        pageNumber: Number(this.list.pageNumber) || 1,
        count: Number(this.list.count) || 10,
      }

      if (this.applied.searchComFermand) params.searchComFermand = this.applied.searchComFermand
      if (this.applied.confirmed !== null) params.confirmed = this.applied.confirmed
      if (this.applied.mediaType) params.mediaType = this.applied.mediaType
      if (Number(this.applied.type)) params.type = Number(this.applied.type)
      if (Number(this.applied.objectId) && Number(this.applied.type)) params.Id = Number(this.applied.objectId)

      return params
    },

    changePage(pageNumber) {
      this.list.pageNumber = Number(pageNumber) || 1
      this.selectedIds = []
      this.getAllComments()
    },

    toggleSelect(id, checked) {
      const ids = new Set(this.selectedIds || [])
      if (checked) ids.add(id)
      else ids.delete(id)
      this.selectedIds = Array.from(ids)
    },

    selectAllRows() {
      const current = Array.isArray(this.tableItems) ? this.tableItems : []
      if (!current.length) return
      if (this.selectedIds.length < current.length) {
        this.selectedIds = Array.from(new Set(current.map((x) => x.commentId)))
      } else {
        this.selectedIds = []
      }
    },

    async getAllComments() {
      try {
        this.isLoading = true
        const req = new GetAll(this)
        req.setRequestParam(this.buildQueryParams())

        await req.fetch((res) => {
          const items = this.extractItems(res)
          const total = this.extractTotal(res)
          items.forEach(item => {
            if (item.type === 5 && (item.masterFullName)) {
              item.objectTitle = item.masterFullName
            } else if (item.type === 3 && (item.title)) {
              item.objectTitle = item.title
            } else if (item.type === 6 && ( item.title)) {
              item.objectTitle = item.title
            }
          })

          this.commentsRaw = items.slice()
          this.totalCount = total === null ? items.length : total
        })
      } catch (e) {
        console.error(e)
        this.$toast({
          component: ToastificationContent,
          props: { title: "خطا در دریافت لیست نظرات", icon: "AlertTriangleIcon", variant: "danger" },
        })
      } finally {
        this.isLoading = false
      }
    },

    refreshList() {
      this.getAllComments()
    },

    openCreateModal() {
      this.editScrollToPreview = false
      this.editingItem = null
      this.showCreateModal = true
    },

    openEditModal(item, scrollToPreview = false) {
      this.editScrollToPreview = scrollToPreview
      this.editingItem = item || null
      this.showCreateModal = true
    },

    onCreateModalHidden() {
      if (!this.showCreateModal) this.editingItem = null
    },

    applyUpdatedRowLocally(updated) {
      const id = Number(updated?.commentId || 0)
      if (!id) return
      const idx = this.commentsRaw.findIndex((x) => Number(x.commentId) === id)
      if (idx === -1) return
      this.commentsRaw.splice(idx, 1, Object.assign({}, this.commentsRaw[idx], updated))
    },

    async submitComment(payload) {
      try {
        this.isLoading = true
        const isEditing = Number(payload?.commentId || 0) > 0

        if (isEditing) {
          const req = new UpdateComment(this)
          req.setRequestParamDataObj(payload)

          await req.fetch((res) => {
            if (!this.normalizeOk(res)) {
              this.$toast({
                component: ToastificationContent,
                props: { title: "ویرایش انجام نشد", icon: "XIcon", variant: "danger" },
              })
              return
            }

            this.$toast({
              component: ToastificationContent,
              props: { title: "نظر با موفقیت ویرایش شد", icon: "CheckcIcon", variant: "success" },
            })

            this.showCreateModal = false
            this.editingItem = null
            this.getAllComments()
          })
          return
        }

        const req = new CreateComment(this)
        req.setRequestParamDataObj(payload)

        await req.fetch(async (res) => {
          if (!this.normalizeOk(res)) {
            this.$toast({
              component: ToastificationContent,
              props: { title: "ایجاد انجام نشد", icon: "XIcon", variant: "danger" },
            })
            return
          }

          this.$toast({
            component: ToastificationContent,
            props: { title: "نظر با موفقیت ایجاد شد", icon: "CheckcIcon", variant: "success" },
          })

          this.list.pageNumber = 1
          this.selectedIds = []
          await this.getAllComments()

          this.showCreateModal = false
        })
      } catch (e) {
        console.error(e)
        this.$toast({
          component: ToastificationContent,
          props: { title: "خطا", icon: "AlertTriangleIcon", variant: "danger" },
        })
      } finally {
        this.isLoading = false
      }
    },

    async toggleConfirm(item, checked) {
      const old = !!item.isConfirmed
      const next = !!checked
      item.isConfirmed = next

      try {
        this.isLoading = true

        const payload = {
          commentId: Number(item.commentId || 0),
          parentId: Number(item.parentId || 0),
          firstName: String(item.firstName || ""),
          lastName: String(item.lastName || ""),
          imageUser: String(item.imageUser || ""),
          message: String(item.message || ""),
          objectId: Number(item.objectId || 0),
          type: Number(item.type || 0),
          mediaType: Number(item.mediaType || 1),
          mediaUrl: String(item.mediaUrl || ""),
          userId: Number(item.userId || 0),
          createDate: item.createDate || new Date().toISOString(),
          isConfirmed: next,
          isVip: !!(item.isVip || item.IsVip),
          priority: Number(item.priority ?? item.Priority ?? 0),
        }

        const req = new UpdateComment(this)
        req.setRequestParamDataObj(payload)

        await req.fetch((res) => {
          if (!this.normalizeOk(res)) {
            item.isConfirmed = old
            this.$toast({
              component: ToastificationContent,
              props: { title: "تغییر وضعیت انجام نشد", icon: "XIcon", variant: "danger" },
            })
          }
        })
      } catch (e) {
        item.isConfirmed = old
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    async deleteComment(item) {
      try {
        const id = Number(item?.commentId || 0)
        if (!id) return

        const ok = window.confirm("آیا از حذف این نظر مطمئن هستید؟")
        if (!ok) return

        this.isLoading = true

        const req = new DeleteBy(this)
        req.setRequestParam({ id })

        await req.fetch(async (res) => {
          if (!this.normalizeOk(res)) {
            this.$toast({
              component: ToastificationContent,
              props: { title: "حذف انجام نشد", icon: "XIcon", variant: "danger" },
            })
            return
          }

          this.$toast({
            component: ToastificationContent,
            props: { title: "نظر حذف شد", icon: "CheckcIcon", variant: "success" },
          })

          const maxPage = Math.max(1, Math.ceil(Math.max(0, this.totalCount - 1) / this.list.count))
          if (this.list.pageNumber > maxPage) this.list.pageNumber = maxPage

          this.selectedIds = this.selectedIds.filter((x) => Number(x) !== id)
          await this.getAllComments()
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.icon-btn {
  border: 0;
  padding: 4px 6px;
  line-height: 1;
}
.icon-btn:focus {
  box-shadow: none !important;
}
.mb-25 {
  margin-bottom: 0.25rem;
}

/* فیلترها در دسکتاپ کنار هم و در صورت کمبود جا به خط بعد می‌روند؛ در موبایل زیر هم (filters-width) */
@media (min-width: 769px) {
  .comment-filter {
    flex: 1 1 170px;
    min-width: 0;
  }
  .comment-filter--picker {
    flex-basis: 240px;
  }
}
.comment-filter--picker ::v-deep .form-group,
.comment-filter--picker ::v-deep .row {
  margin-bottom: 0;
}

/* فاصله‌ی پیش‌فرض قالب (۲rem دو طرف) زیاد است؛ با این مقدار جدول بدون اسکرول افقی جا می‌شود */
.comments-table ::v-deep th,
.comments-table ::v-deep td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}
.comments-table ::v-deep th {
  white-space: nowrap;
}

.comment-cell {
  min-width: 200px;
  max-width: 340px;
}

.comment-product {
  display: block;
  max-width: 170px;
}

.comment-media-badge {
  border: 0;
  cursor: pointer;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.2rem;
}

.comment-date {
  font-size: 0.75rem;
  color: #82868b;
  white-space: nowrap;
}

.comment-text {
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: pre-line;
  word-break: break-word;
}

.comment-text--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
}

.comment-full-text {
  margin: 0;
  line-height: 1.9;
  white-space: pre-line;
  word-break: break-word;
}

.comment-more {
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.8rem;
  color: #7367f0;
}

</style>